/** @format */
import { css } from 'styled-components';

import {
    largeDesktop,
    largeTablet,
    mobile,
    smallMobile,
    smallTablet,
} from '@toucan/client/global/application';
import { theme } from '@toucan/client/global/theme';

import { StyledOriginalContentHeaderProps } from './index';
import { getBackgroundStyle } from './style-helpers';

/* eslint-disable @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example. */
const baseStyles = ({
    variant,
    hasBreadcrumb,
    trustpilotEnabled,
}: StyledOriginalContentHeaderProps) => css`
    ${theme.typography.body_large}
    color: var(--colors-text-inverse);
    background: ${getBackgroundStyle(variant)};

    .content {
        display: grid;
        min-height: 52rem;
        grid-column-gap: var(--layout-spacing-24);
        grid-template-areas:
            'breadcrumbs image'
            'summary image';
        grid-template-rows: 12rem 1fr;
        grid-template-columns: 1fr 1fr;
    }

    .breadcrumbs {
        grid-area: breadcrumbs;
        margin-top: var(--layout-spacing-8);
    }

    .breadcrumb-skeleton {
        width: 10rem;
    }

    .breadcrumb-skeleton-touch {
        display: none;
        margin-bottom: var(--layout-spacing-24);
        .skeleton {
            width: 3.2rem;
            height: 3.2rem;
        }
    }

    .summary {
        grid-area: summary;
        align-self: flex-start;
        margin-bottom: var(--layout-spacing-24);
    }

    .text {
        margin-bottom: var(--layout-spacing-16);
    }

    .cta {
        width: fit-content;
        margin-top: var(--layout-spacing-24);
    }

    .cta-skeleton {
        margin-top: var(--layout-spacing-24);
        width: 50%;
    }

    .trustpilot-container {
        max-width: 100%;
        margin-top: var(--layout-spacing-72);
    }

    .image {
        grid-area: image;
        overflow: hidden;
        max-height: 52rem;
        justify-self: center;
        align-self: flex-end;
        font-size: 0;
    }

    .image-skeleton {
        grid-area: image;
        overflow: hidden;
        justify-self: center;
        align-self: center;

        .skeleton {
            position: static;
            width: 30rem;
            height: 30rem;
        }
    }

    .trustpilot-skeleton {
        display: ${trustpilotEnabled ? 'block' : 'none'};
    }

    ${largeDesktop(css`
        .content {
            grid-template-rows: 12rem 1fr;
        }

        .image-skeleton .skeleton {
            width: 40rem;
            height: 40rem;
        }
    `)}

    ${largeTablet(css`
        .content {
            grid-column-gap: var(--layout-spacing-16);
            min-height: 45rem;
            grid-template-rows: 7rem 1fr;
        }

        .summary {
            margin-bottom: var(--layout-spacing-48);
        }

        .image {
            max-height: 45rem;
        }

        .image-skeleton .skeleton {
            width: 26rem;
            height: 26rem;
        }

        .breadcrumbs {
            margin-top: var(--layout-spacing-24);
        }
    `)}

    ${smallTablet(css`
        overflow: visible;

        .content {
            display: block;
            min-height: unset;
            ${
                !hasBreadcrumb &&
                css`
                    padding-top: var(--layout-spacing-24);
                `
            }
        }

        .summary {
            margin-bottom: var(--layout-spacing-24);
        }

        .cta,
        .cta button {
            width: 100%;
        }

        .image {
            img {
                display: block;
                object-fit: cover;
                object-position: center top;
                height: 37rem;
                width: 100%;
            }
        }

        .image-skeleton .skeleton {
            width: 37rem;
            height: 37rem;
            margin-bottom: var(--layout-spacing-32);
        }

        .breadcrumb-skeleton {
            display: none;
        }

        .breadcrumb-skeleton-touch {
            display: block;
        }

        .trustpilot-container {
            width: 100%;
        }
    `)}

    ${mobile(css`
        .image {
            img {
                display: block;
                object-fit: cover;
                object-position: center top;
                height: 32rem;
                width: 100%;
            }
        }

        .image-skeleton .skeleton {
            width: 38rem;
            height: 32rem;
            margin-bottom: var(--layout-spacing-24);
        }
    `)}

    ${smallMobile(css`
        .image {
            img {
                display: block;
                object-fit: cover;
                object-position: center top;
                height: 24rem;
                width: 100%;
            }
        }

        .image-skeleton .skeleton {
            width: 29rem;
            height: 24rem;
            margin-bottom: var(--layout-spacing-24);
        }
    `)}
`;
/* eslint-enable @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example. */

export const styles = (props: StyledOriginalContentHeaderProps) => css`
    ${baseStyles(props)}
`;
